/**
 * API utility -- centralized fetch wrapper with auth.
 * Auth is handled via HttpOnly secure cookie (set by server on login).
 * Cookie is sent automatically with credentials: 'include'.
 */

const API_BASE = process.env.REACT_APP_API_URL || '/api';

class ApiError extends Error {
  constructor(message, status, details) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

/**
 * Global 401 handler -- when any API call gets a 401, redirect to login.
 * Skip redirect for the /auth/me check itself (AuthContext handles that).
 */
function handleUnauthorized(endpoint) {
  // Don't redirect for the initial auth check -- AuthContext handles that transition
  if (endpoint === '/auth/me') return;

  // Redirect to login if we're not already there
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

async function request(endpoint, options = {}) {
  const config = {
    credentials: 'include', // Send HttpOnly cookies with every request
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  if (config.body && typeof config.body === 'object') {
    config.body = JSON.stringify(config.body);
  }

  const response = await fetch(`${API_BASE}${endpoint}`, config);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));

    if (response.status === 401) {
      handleUnauthorized(endpoint);
    }

    const apiError = new ApiError(error.error || 'Request failed', response.status, error.details);
    apiError.fields = error.fields || null;
    throw apiError;
  }

  return response.json();
}

// Convenience methods
const api = {
  get: (endpoint) => request(endpoint),
  post: (endpoint, data) => request(endpoint, { method: 'POST', body: data }),
  put: (endpoint, data) => request(endpoint, { method: 'PUT', body: data }),
  patch: (endpoint, data) => request(endpoint, { method: 'PATCH', body: data }),
  delete: (endpoint, data) => request(endpoint, { method: 'DELETE', body: data }),
};

export default api;
export { ApiError };
